import _ from 'lodash';

import Dinero from 'dinero.js';

/**
 * StatementUtils
 *
 * Universal utility functions for contact statements.
 * Helper functions that work across all contact types.
 */

const getDinero = (amount, currency, defaultCurrency) =>
    Dinero({ amount: Math.round((amount ? amount : 0) * 100), currency: currency || defaultCurrency });
const getDineroCent = (amount, currency, defaultCurrency) =>
    Dinero({ amount: Math.round(amount ? amount : 0), currency: currency || defaultCurrency });

export const getStatementDescription = (transaction) => {
    let description = '';
    if (transaction.Statements.length > 0) {
        if (transaction.Statements[0].Description) {
            description = transaction.Statements[0].Description;
        }
        if (transaction.Statements.length > 1) {
            if (transaction.Statements[1].Description && transaction.Statements[1].Description !== description) {
                description += `\n${transaction.Statements[1].Description}`;
            }
        }
    }
    if (!description) {
        description = transaction.Description;
    }
    return description;
};

export const getModelStatementDescription = (transaction) => {
    let description = '';
    if (transaction.ModelStatements.length > 0) {
        if (transaction.ModelStatements[0].Description) {
            description = transaction.ModelStatements[0].Description;
        }
    }
    if (!description) {
        description = transaction.Description;
    }
    return description;
};

export const getFilteredStatementTotals_OLD = (activeTransactions, defaultCurrency, statementType, type = 'taxed') => {
    //type can be "taxed" or "untaxed" or "taxes"

    const totals = [];
    if (activeTransactions.length > 0) {
        const statementCurrencies = activeTransactions.map((item) => item.Currency);
        statementCurrencies.push(defaultCurrency);
        const currencies = [...new Set(statementCurrencies)];
        for (const cur of currencies) {
            totals.push({
                Currency: cur,
                isDefaultCurrency: cur === defaultCurrency,
                Dinero: getDinero(0, cur, defaultCurrency),
            });
        }
        for (const transactionItem of activeTransactions) {
            for (const statementItem of transactionItem.Statements) {
                if (transactionItem.Currency !== defaultCurrency && !statementItem.TaxedAmountDef) {
                    const valueToSum =
                        type === 'taxed'
                            ? statementItem.TaxedAmount
                            : type === 'untaxed'
                              ? statementItem.Amount
                              : statementItem.TaxedAmount - statementItem.Amount;
                    const defcuritem = _.find(totals, { Currency: transactionItem.Currency });
                    defcuritem.Dinero = defcuritem.Dinero.add(
                        getDineroCent(valueToSum * 100, transactionItem.Currency, defaultCurrency)
                    );
                    defcuritem.isDefaultCurrency = false;
                } else {
                    const defcuritem = _.find(totals, { Currency: defaultCurrency });
                    if (statementItem.TaxedAmountDef) {
                        const valueToSum =
                            type === 'taxed'
                                ? statementItem.TaxedAmountDef
                                : type === 'untaxed'
                                  ? statementItem.AmountDef
                                  : statementItem.TaxedAmountDef - statementItem.AmountDef;
                        defcuritem.Dinero = defcuritem.Dinero.add(getDineroCent(valueToSum * 100, defaultCurrency));
                    } else {
                        const valueToSum =
                            type === 'taxed'
                                ? statementItem.TaxedAmount
                                : type === 'untaxed'
                                  ? statementItem.Amount
                                  : statementItem.TaxedAmount - statementItem.Amount;
                        defcuritem.Dinero = defcuritem.Dinero.add(getDineroCent(valueToSum * 100, defaultCurrency));
                    }
                    if (
                        statementItem.PaidStatus &&
                        statementItem.PaidStatus.toLowerCase() === 'paid' &&
                        type !== 'taxes'
                    ) {
                        switch (statementType) {
                            case 'Model':
                            case 'Agency':
                            case 'Service':
                                if (statementItem.PaidAmount && statementItem.PaidAmount !== 0) {
                                    defcuritem.Dinero = defcuritem.Dinero.subtract(
                                        getDineroCent(statementItem.PaidAmount * 100, defaultCurrency)
                                    );
                                }
                                break;
                            default:
                                if (statementItem.TaxedAmountDef) {
                                    defcuritem.Dinero = defcuritem.Dinero.subtract(
                                        getDineroCent(statementItem.TaxedAmountDef * 100, defaultCurrency)
                                    );
                                } else {
                                    defcuritem.Dinero = defcuritem.Dinero.subtract(
                                        getDineroCent(statementItem.TaxedAmount * 100, defaultCurrency)
                                    );
                                }
                                break;
                        }
                    }
                    defcuritem.isDefaultCurrency = true;
                }
            }
        }
    } else {
        totals.push({
            Currency: defaultCurrency,
            isDefaultCurrency: true,
            Dinero: getDinero(0, defaultCurrency, defaultCurrency),
        });
    }
    return totals;
};

/**
 * Calculate the difference between end and start balances for each currency
 * @param {Object} startBalances - Transformed balances { Balances: { USD: 2675, EUR: 400 }, AvailableBalances: { USD: 2675, EUR: 300 } }
 * @param {Object} endBalances - Transformed balances { Balances: { USD: 2675, EUR: 400 }, AvailableBalances: { USD: 2675, EUR: 300 } }
 * @param {string} defaultCurrency - The default currency code
 * @param {boolean} available - If true, use AvailableBalances; otherwise use Balances
 * @returns {Array} Delta by currency [{ Currency: 'USD', isDefaultCurrency: true, Dinero: {...} }]
 */
export const getPeriodDelta = (startBalances, endBalances, defaultCurrency, available) => {
    const balanceKey = available ? 'AvailableBalances' : 'Balances';
    const result = [];

    const startObj = startBalances?.[balanceKey] || {};
    const endObj = endBalances?.[balanceKey] || {};

    // Get all currencies from both start and end balances
    const allCurrencies = new Set([...Object.keys(startObj), ...Object.keys(endObj)]);

    let transactionCounter = 1;
    for (const currency of allCurrencies) {
        const startValue = startObj[currency] || 0;
        const endValue = endObj[currency] || 0;

        const startDinero = getDinero(startValue, currency, defaultCurrency);
        const endDinero = getDinero(endValue, currency, defaultCurrency);
        const deltaDinero = endDinero.subtract(startDinero);

        result.push({
            Currency: currency,
            isDefaultCurrency: currency === defaultCurrency,
            Dinero: deltaDinero,
            transactionCount: transactionCounter,
        });
        transactionCounter++;
    }

    return result;
};

export const getFilteredStatementTotals = (activeTransactions, defaultCurrency, statementType) => {
    const totals = [];
    if (activeTransactions.length > 0) {
        const statementCurrencies = activeTransactions.map((item) =>
            item.PaymentCurrency ? item.PaymentCurrency : item.Currency
        );
        statementCurrencies.push(defaultCurrency);

        const currencies = [...new Set(statementCurrencies)];
        for (const cur of currencies) {
            totals.push({
                Currency: cur,
                isDefaultCurrency: cur === defaultCurrency,
                Dinero: getDinero(0, cur, defaultCurrency),
                transactionCount: 0,
            });
        }
        for (const transactionItem of activeTransactions) {
            if (transactionItem.isPayment) {
                const defcuritem = _.find(totals, { Currency: transactionItem.PaymentCurrency });
                defcuritem.Dinero = defcuritem.Dinero.subtract(
                    getDineroCent(transactionItem.PaymentAmount * 100, transactionItem.PaymentCurrency, defaultCurrency)
                );
                defcuritem.isDefaultCurrency = transactionItem.PaymentCurrency === defaultCurrency;
                defcuritem.transactionCount++;
            } else {
                for (const statementItem of transactionItem.Statements) {
                    if (transactionItem.Currency !== defaultCurrency && !statementItem.TaxedAmountDef) {
                        const valueToSum = statementItem.TaxedAmount;
                        const defcuritem = _.find(totals, { Currency: transactionItem.Currency });
                        defcuritem.Dinero = defcuritem.Dinero.add(
                            getDineroCent(valueToSum * 100, transactionItem.Currency, defaultCurrency)
                        );
                        defcuritem.isDefaultCurrency = false;
                        defcuritem.transactionCount++;
                    } else {
                        const defcuritem = _.find(totals, { Currency: defaultCurrency });
                        if (statementItem.TaxedAmountDef) {
                            const valueToSum = statementItem.TaxedAmountDef;
                            defcuritem.Dinero = defcuritem.Dinero.add(getDineroCent(valueToSum * 100, defaultCurrency));
                        } else {
                            const valueToSum = statementItem.TaxedAmount;
                            defcuritem.Dinero = defcuritem.Dinero.add(getDineroCent(valueToSum * 100, defaultCurrency));
                        }
                        defcuritem.isDefaultCurrency = true;
                        defcuritem.transactionCount++;
                    }
                }
            }
        }
    } else {
        totals.push({
            Currency: defaultCurrency,
            isDefaultCurrency: true,
            Dinero: getDinero(0, defaultCurrency, defaultCurrency),
            transactionCount: 0,
        });
    }
    return totals;
};
